import { CARD_PLATFORM_MENU_KEY } from './cardPlatformRoutes.ts'

export type SidebarIconKey =
  | 'activity'
  | 'alert-triangle'
  | 'bar-chart'
  | 'clipboard-check'
  | 'clipboard-list'
  | 'credit-card'
  | 'dollar-sign'
  | 'external-link'
  | 'file-search'
  | 'globe'
  | 'import'
  | 'landmark'
  | 'monitor'
  | 'network'
  | 'pie-chart'
  | 'power'
  | 'rotate-ccw'
  | 'scale'
  | 'shield-alert'
  | 'shield-check'
  | 'trending-up'
  | 'user-cog'
  | 'users'
  | 'wallet'
  | 'wallet-cards'

export interface SidebarNavigationItem {
  key: string
  label: string
  icon: SidebarIconKey
}

export interface SidebarNavigationGroup {
  key: string
  label: string
  icon: SidebarIconKey
  children: SidebarNavigationItem[]
}

export const sidebarNavigationGroups: SidebarNavigationGroup[] = [
  {
    key: 'dashboards',
    label: '数据看板',
    icon: 'pie-chart',
    children: [
      { key: '/analytics', icon: 'trending-up', label: '经营分析' },
      { key: '/fund-monitor', icon: 'activity', label: '客户资金监控' },
      { key: '/account-analysis', icon: 'bar-chart', label: '账户分析' },
      { key: '/consumptions', icon: 'bar-chart', label: '消耗数据' },
      { key: '/port-account-dashboard', icon: 'dollar-sign', label: '端口账户看板' },
    ],
  },
  {
    key: 'customer-funds',
    label: '客户与资金',
    icon: 'wallet',
    children: [
      { key: '/customers', icon: 'users', label: '客户管理' },
      { key: '/payments', icon: 'dollar-sign', label: '资金管理' },
      { key: '/refunds', icon: 'rotate-ccw', label: '退款管理' },
      { key: '/receiving-accounts', icon: 'landmark', label: '收款信息' },
    ],
  },
  {
    key: 'advertising-accounts',
    label: '广告账户',
    icon: 'monitor',
    children: [
      { key: '/ad-accounts', icon: 'monitor', label: '广告账户' },
      { key: '/bm', icon: 'network', label: 'BM管理' },
    ],
  },
  {
    key: 'operations',
    label: '业务操作',
    icon: 'clipboard-check',
    children: [
      { key: '/opening-orders', icon: 'clipboard-list', label: '开户待办' },
      { key: '/media-todos', icon: 'clipboard-check', label: 'ATMedia待办' },
      { key: '/batch-import', icon: 'import', label: '批量导入' },
      { key: '/approvals', icon: 'clipboard-check', label: '审批管理' },
      { key: '/reconcile', icon: 'scale', label: '对账' },
      { key: '/staging', icon: 'alert-triangle', label: '异常处置' },
    ],
  },
  {
    key: 'cards',
    label: '虚拟卡',
    icon: 'credit-card',
    children: [
      { key: CARD_PLATFORM_MENU_KEY, icon: 'wallet-cards', label: '卡台管理' },
      { key: '/card-spend-reconcile', icon: 'wallet-cards', label: '卡片消耗差异' },
      { key: '/cashback-rates', icon: 'credit-card', label: '卡返现费率' },
    ],
  },
  {
    key: 'compliance-system',
    label: '系统管理',
    icon: 'shield-alert',
    children: [
      { key: '/policies', icon: 'shield-check', label: '政策与历史' },
      { key: '/audit-logs', icon: 'file-search', label: '审计日志' },
      { key: '/users', icon: 'user-cog', label: '用户管理' },
      { key: '/admin/kill-switch', icon: 'power', label: 'Kill Switch' },
    ],
  },
  {
    key: 'external-tools',
    label: '外部工具',
    icon: 'external-link',
    children: [
      { key: '/extension', icon: 'globe', label: 'FB 工具' },
    ],
  },
]

export function getSidebarGroupsForPaths(
  allowedPaths: readonly string[],
): SidebarNavigationGroup[] {
  const allowed = new Set(allowedPaths)

  return sidebarNavigationGroups
    .map(group => ({
      ...group,
      children: group.children.filter(child => allowed.has(child.key)),
    }))
    .filter(group => group.children.length > 0)
}

function normalizeSidebarSearch(value: string): string {
  return value.toLocaleLowerCase().replace(/\s+/g, '')
}

export function matchesSidebarSearch(label: string, query: string): boolean {
  const normalizedQuery = normalizeSidebarSearch(query)
  return normalizedQuery.length === 0
    || normalizeSidebarSearch(label).includes(normalizedQuery)
}

export function searchSidebarGroups(
  groups: readonly SidebarNavigationGroup[],
  query: string,
): SidebarNavigationGroup[] {
  if (normalizeSidebarSearch(query).length === 0) return [...groups]

  return groups
    .map(group => ({
      ...group,
      children: matchesSidebarSearch(group.label, query)
        ? group.children
        : group.children.filter(child => matchesSidebarSearch(child.label, query)),
    }))
    .filter(group => group.children.length > 0)
}
